import React, { useCallback, useContext, useMemo, useState } from 'react'
import axios from 'axios'
import { getAccessToken, getRefreshToken, removeTokens, setAccessToken, setRefreshToken } from '../storage/auth'
import { useApiFormAuthImpersonate, useApiFormAuthImpersonateRedeem, useApiFormAuthLogout } from '../api-new/state/auth'
import { useApiFormAuthRefresh } from '../api-new/state/auth'
import { useLocation } from 'react-router-dom'
import { decodeJwtClaims, isLaunchToken } from '../shared/jwt'
import { isOrganizationUnavailable } from '../shared/error/server-message'
import { leaveAfterLogout, loginUrlFor, logoutBody, refreshEndReason, sessionEndReason, stashLogoutReason } from './logout-destination'

export const AuthContext = React.createContext()

export const useAuthContext = () => useContext(AuthContext)

export const AuthProvider = ({ children }) => {
  const [jwtClaims, updateJwtClaims] = useState({})

  const state = useMemo(
    () => {
      return {
        get isAuth() {
          return !!this.accessToken
        },

        get accessToken() {
          return getAccessToken()
        },
        get refreshToken() {
          return getRefreshToken()
        },

        jwtClaims: jwtClaims,
        role: jwtClaims.role, //remove
        loginID: jwtClaims.loginID,
        userID: jwtClaims.userID,
        isSysAdmin: jwtClaims.isSysAdmin || false,
        isSysRoot: jwtClaims.isSysRoot || false,
        isSession: jwtClaims.isSession || false,
        // The owner's restricted restore session (delete organization, spec
        // §2.5): the organization is scheduled for deletion and this token
        // reaches only the restore page's routes.
        orgPendingDeletion: jwtClaims.orgPendingDeletion || false,

        organizationID: jwtClaims.organizationID,
        organizationName: jwtClaims.organizationName,
        organizationType: jwtClaims.organizationType,

        featureAnalytic: jwtClaims.featureAnalytic,
        featureHeatmap: jwtClaims.featureHeatmap,
        featureTradeShow: jwtClaims.featureTradeShow,
        featureAI: jwtClaims.featureAI,
        featureSSO: jwtClaims.featureSSO
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [jwtClaims]
  )

  const setAuthToken = authToken => {
    const jwtClaims = decodeJwtClaims(authToken && authToken.accessToken)
    if (!jwtClaims) return
    // A bounded LMS session (client lms-launch, Phase F F-5) belongs to the
    // viewer and the app: this app stores neither of its tokens and reads
    // none of its claims.
    if (isLaunchToken(authToken.accessToken)) return

    updateJwtClaims(jwtClaims)

    setAccessToken(authToken.accessToken, jwtClaims.isSession)
    setRefreshToken(authToken.refreshToken, jwtClaims.isSession)
  }

  const actions = {
    clearJwtClaims: () => updateJwtClaims({}),

    // The provider-link sign-in flow used to stash the IdP's end-session URL
    // here for the hidden iframe to load on sign-out. The server composes
    // that URL itself now (see logout-destination.js), so nothing writes
    // this key anymore -- removeSignoutURL only clears a value a pre-upgrade
    // tab may have left behind.
    removeSignoutURL: () => sessionStorage.removeItem('signout-url'),

    setAuthToken: authToken => setAuthToken(authToken),

    checkExpRefresh: async () => {
      let now = Math.floor(Date.now() / 1000)
      if (now > state.jwtClaims.exp) {
        await actions.refresh()
      }
    }
  }

  return (
    <AuthContext.Provider value={{ actions: actions, state: state, setAuthToken: setAuthToken }}>
      {children}
    </AuthContext.Provider>
  )
}

// A refresh that comes back 401 (expired, idle, revoked from elsewhere)
// ends the session here (refreshRecreate, a deep-link hand-off, only on the
// server's own reasons) -- tokens and claims cleared, the reason stashed,
// sign-in with a notice -- and resolves, so no caller is left holding an
// unhandled rejection. Anything else (the network) is thrown to the caller.
export const useRefresh = () => {
  // Where signing in again goes: the page AND its query -- a launch link's
  // ?lt= is the whole point of the page (plan Task 20 fix round 1, S3).
  const { pathname, search } = useLocation()
  const here = `${pathname}${search}`
  const authContext = useAuthContext()
  const apiFormAuthRefresh = useApiFormAuthRefresh()

  const refresh = useCallback(async () => {
    try {
      if (authContext.state.isAuth) {
        const authToken = await apiFormAuthRefresh.get(authContext.state.refreshToken)
        authContext.setAuthToken(authToken)
      }
    } catch (err) {
      const reason = refreshEndReason(err)
      if (reason) {
        stashLogoutReason(reason)
        removeTokens(authContext.state.isSession)
        authContext.actions.clearJwtClaims()
        window.location.replace(loginUrlFor(here, reason))
      } else {
        throw err
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [here])

  const refreshRecreate = useCallback(async refreshToken => {
    try {
      const authToken = await apiFormAuthRefresh.get(`${refreshToken}/recreate`)
      authContext.setAuthToken(authToken)
    } catch (err) {
      // A deep-link hand-off: only the server's own session-end reasons
      // (expired, idle, max, impersonation_limit) end the session. A 401
      // with no reason is a bad hand-off token, not this tab's session:
      // keep the tab's tokens and let the caller show its error banner.
      const reason = sessionEndReason(err)
      if (reason) {
        stashLogoutReason(reason)
        removeTokens(authContext.state.isSession)
        authContext.actions.clearJwtClaims()
        window.location.replace(loginUrlFor(here, reason))
      } else {
        throw err
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [here])

  return { refresh, refreshRecreate }
}

// The impersonated tab redeems its one-time hand-off code for the session's
// tokens (POST /auth-impersonate/redeem). Single use: a second redeem, an
// expired code, or anything that is not such a code is refused.
export const useRedeemImpersonation = () => {
  const authContext = useAuthContext()
  const apiFormAuthImpersonateRedeem = useApiFormAuthImpersonateRedeem()

  return useCallback(async code => {
    const authToken = await apiFormAuthImpersonateRedeem.post({ code })
    authContext.setAuthToken(authToken)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

// The sign-out request once the session's organization has been switched
// off (delete organization): every service's organization gate answers a
// request CARRYING that access token with 401 organization_unavailable, the
// sign-out included. POST /auth-logout needs only the refresh token in its
// body, so this one retry is sent without the Authorization header.
const postLogoutWithoutAccessToken = async body =>
  (await axios.post(`${process.env.REACT_APP_API_AUTH}/api/v1/auth-logout`, body, { headers: { 'Content-Type': 'application/json' } })).data

// One retry; a gate refusal on either attempt (the gate can start refusing
// between them) is sent once more without the access token.
export const revokeSession = async (apiFormAuthLogout, body) => {
  const attempt = async () => {
    try {
      return { result: await apiFormAuthLogout.post(body) }
    } catch (err) {
      if (isOrganizationUnavailable(err)) return { result: await postLogoutWithoutAccessToken(body) }
      return { err }
    }
  }
  const first = await attempt()
  if (!first.err) return first.result
  const second = await attempt()
  if (second.err) throw second.err
  return second.result
}

export const useLogout = () => {
  const { pathname, search } = useLocation()
  const authContext = useAuthContext()
  const apiFormAuthLogout = useApiFormAuthLogout()

  // returnPath: where signing in again goes (default: this page and its
  // query). The delete-organization page sends the owner to the restore page.
  return async (reason, returnPath) => {
    // Revoke server-side FIRST. The response carries where to send the browser
    // to end the IdP session too. A failed revoke is surfaced, not swallowed:
    // clearing tokens locally while the refresh token stays valid server-side
    // is a half-logout the user cannot see.
    let result = null
    try {
      result = await revokeSession(apiFormAuthLogout, logoutBody(authContext.state.refreshToken, authContext.state.jwtClaims))
    } catch (second) {
      window.alert('Sign-out could not reach the server. Your session was cleared on this device only.')
    }

    removeTokens(authContext.state.isSession)
    authContext.actions.removeSignoutURL() // legacy key; the server composes the URL now

    stashLogoutReason(reason) // survives the guard's redirect race, see logout-destination.js

    leaveAfterLogout({
      idpLogoutUrl: result && result.idpLogoutUrl,
      loginUrl: loginUrlFor(returnPath || `${pathname}${search}`, reason),
      clearClaims: authContext.actions.clearJwtClaims,
      navigate: url => window.location.replace(url)
    })
  }
}

// Sign out, and come back to `path` after signing in again.
export const useSignOutTo = () => {
  const logout = useLogout()
  return path => logout(null, path)
}

export const useLogoutDeletedAccount = () => {
  const authContext = useAuthContext()
  const apiFormAuthLogout = useApiFormAuthLogout()

  return async () => {
    // Same revoke-then-navigate shape as useLogout, minus the redirect param
    // -- once the account is gone there is no page to return to.
    let result = null
    try {
      result = await apiFormAuthLogout.post(logoutBody(authContext.state.refreshToken, authContext.state.jwtClaims))
    } catch (first) {
      try {
        result = await apiFormAuthLogout.post(logoutBody(authContext.state.refreshToken, authContext.state.jwtClaims))
      } catch (second) {
        window.alert('Sign-out could not reach the server. Your session was cleared on this device only.')
      }
    }

    removeTokens(authContext.state.isSession)
    authContext.actions.removeSignoutURL() // legacy key; the server composes the URL now

    leaveAfterLogout({
      idpLogoutUrl: result && result.idpLogoutUrl,
      loginUrl: loginUrlFor(null, null),
      clearClaims: authContext.actions.clearJwtClaims,
      navigate: url => window.location.replace(url)
    })
  }
}


export const useImpersonateUser = () => {
  const apiFormAuthImpersonate = useApiFormAuthImpersonate()

  return useCallback(async (organizationID, userID) => {
    return await apiFormAuthImpersonate.post({ organizationID, userID })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
